import React, { useEffect, useState, useRef } from "react";
import { Users } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Thin live-status bar shown between the gold ticker and the brands grid.
 * Shows current club member count with a soft session-only auto-increment
 * so it feels alive on the page.
 */
export default function LiveStatusBar() {
  const [count, setCount] = useState(null);
  const [displayCount, setDisplayCount] = useState(0);
  const animRef = useRef(null);

  // Pull the real number on mount
  useEffect(() => {
    api
      .get("/club/live-stats")
      .then((r) => setCount(r.data.member_count))
      .catch(() => {});
  }, []);

  // Count-up animation when we get the number
  useEffect(() => {
    if (count == null) return;
    cancelAnimationFrame(animRef.current);
    const start = performance.now();
    const from = displayCount;
    const to = count;
    const dur = 1400;
    const step = (now) => {
      const t = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
      setDisplayCount(Math.round(from + (to - from) * eased));
      if (t < 1) animRef.current = requestAnimationFrame(step);
    };
    animRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(animRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count]);

  // Slow drift +1 every 60–180s so the count feels live during the session
  useEffect(() => {
    if (count == null) return;
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      const delay = 60000 + Math.random() * 120000;
      setTimeout(() => {
        if (cancelled) return;
        setCount((c) => (c == null ? c : c + 1));
        tick();
      }, delay);
    };
    tick();
    return () => {
      cancelled = true;
    };
  }, [count]);

  if (count == null) return null;

  return (
    <div
      className="relative bg-[var(--surface)] border-b border-[var(--border)]"
      data-testid="live-status-bar"
    >
      <div className="container-nk py-2.5 flex flex-wrap items-center justify-center gap-x-5 gap-y-1 text-center">
        <span className="inline-flex items-center gap-2 font-mono text-[10.5px] uppercase tracking-[0.2em] text-gold">
          <span className="relative inline-flex w-2 h-2">
            <span className="absolute inset-0 rounded-full bg-[var(--gold)] animate-ping opacity-70" />
            <span className="relative inline-block w-2 h-2 rounded-full bg-[var(--gold)]" />
          </span>
          Live
        </span>
        <span className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.16em] text-[var(--text)]/85">
          <Users size={12} className="text-gold" />
          <span data-testid="live-member-count" className="tabular-nums text-[var(--text)] font-medium">
            {displayCount.toLocaleString()}
          </span>
          Club Members &amp; Growing
        </span>
        <span className="hidden md:inline font-mono text-[10.5px] uppercase tracking-[0.18em] text-muted">
          ·  Edinburgh ·  Updated just now
        </span>
      </div>
    </div>
  );
}
